/**
 * =================================================================
 * SCRIPT-NAME:   kdi_tracker.js
 * BESCHREIBUNG:  React-Komponente zur automatischen Analyse und
 *                Visualisierung von Google Ads Auktionsdaten.
 *
 * VERSION:       v4.2.1
 * DATUM:         2025-08-23 22:40:00
 *
 * Änderungen (v4.2.1):
 * - Globaler Randabstand: Alle Inhalte beginnen/enden ~0,5 cm vom Rand.
 * - Notizbereich: In eine eigene Karte ausgelagert und scrollbar.
 * - Buttons im Formular: Klar differenziert (Blau = Hauptaktion, Grau = Zurücksetzen)
 *   und mit mehr Abstand zueinander.
 * - Chart-UseEffect: Doppelte Deklaration von minDate/maxDate entfernt (jetzt extentDates).
 * - Kopfzeile: Dynamische Uhrzeit/Datum + Version.
 * - Bestehende Funktionalität beibehalten (Fetch/Export/Toggles/Notes-Linien im Chart).
 * =================================================================
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";

// ====== Konfiguration ======
const GET_DATA_URL = "/api/kdi_get_data.php";
const PROCESS_DATA_URL = "/api/kdi_processor.php";
const GET_NOTES_URL = "/api/kdi_notes_handler.php";
const SAVE_NOTE_URL = "/api/kdi_notes_handler.php";
const PRIMARY_LABEL = "Sie";
const VERSION = "v4.2.1";

// ====== Utilities ======
const cls = (...c) => c.filter(Boolean).join(" ");
const fmtPct = (v) => (Number.isFinite(v) ? `${v.toFixed(2)}%` : "–");

const dateParser = d3.utcParse("%Y-%m-%d");
const parseISO = (s) => dateParser(s);

function useResizeObserver(targetRef) {
  const [rect, setRect] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const el = targetRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setRect({ width, height });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [targetRef]);
  return rect;
}

function usePersistentState(key, initial) {
  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(initial)) {
          return Array.isArray(parsed) ? parsed : initial;
        }
        return parsed;
      }
      return initial;
    } catch {
      return initial;
    }
  });

  useEffect(() => {
    try {
      const toSave = state instanceof Set ? Array.from(state) : state;
      localStorage.setItem(key, JSON.stringify(toSave));
    } catch (e) {
      console.error(`Fehler beim Speichern im localStorage für Key "${key}"`, e);
    }
  }, [key, state]);

  return [state, setState];
}

function toCSV(rows) {
  if (!rows?.length) return "";
  const keys = Object.keys(rows[0]);
  const head = keys.join(",");
  const body = rows
    .map((r) =>
      keys
        .map((k) =>
          typeof r[k] === "string" && r[k].includes(",")
            ? `"${String(r[k]).replaceAll('"', '""')}"`
            : r[k]
        )
        .join(",")
    )
    .join("\n");
  return `${head}\n${body}`;
}

function downloadBlob(filename, mime, content) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function downloadPNGFromSVG(svgEl, filename = "chart.png") {
  if (!svgEl) return;
  const serializer = new XMLSerializer();
  const svgStr = serializer.serializeToString(svgEl);
  const svgBlob = new Blob([svgStr], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(svgBlob);

  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement("canvas");
    const bbox = svgEl.viewBox.baseVal;
    const w = bbox?.width || svgEl.clientWidth || 1200;
    const h = bbox?.height || svgEl.clientHeight || 480;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    canvas.toBlob((blob) => {
      const dl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dl;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(dl);
    });
    URL.revokeObjectURL(url);
  };
  img.src = url;
}

// ====== Chart-Komponente ======
function LinesChart({
  data,
  hidden,
  onHover,
  highlight = PRIMARY_LABEL,
  notes = [],
}) {
  const wrapRef = useRef(null);
  const svgRef = useRef(null);
  const noteTooltipRef = useRef(null);
  const { width: containerWidth } = useResizeObserver(wrapRef);

  const margin = { top: 20, right: 180, bottom: 44, left: 56 };
  const height = 420;
  const innerH = height - margin.top - margin.bottom;

  const dynamicWidth = Math.max(
    containerWidth - margin.left - margin.right,
    data.length * 30
  );
  const innerW = dynamicWidth;

  const parsedNotes = useMemo(
    () =>
      notes
        .map((n) => ({
          ...n,
          dateObj: parseISO(n.note_date),
        }))
        .filter((n) => n.dateObj),
    [notes]
  );

  const { longData, series, bySeries } = useMemo(() => {
    const long = [];
    const s = new Set();
    (data || []).forEach((d) => {
      const dt = parseISO(d.date);
      if (dt) {
        Object.entries(d.performance || {}).forEach(([domain, value]) => {
          s.add(domain);
          long.push({ date: dt, domain, value: +value });
        });
      }
    });
    const grouped = d3.group(long, (d) => d.domain);
    return {
      longData: long,
      series: Array.from(s),
      bySeries: grouped,
    };
  }, [data]);

  const color = useMemo(() => {
    const palette = d3.schemeTableau10;
    const ordered = [
      highlight,
      ...series.filter((s) => s !== highlight).sort(),
    ];
    const map = new Map();
    ordered.forEach((name, i) =>
      map.set(name, i === 0 ? "#4338ca" : palette[i % palette.length])
    );
    return (name) => map.get(name) || "#999";
  }, [series, highlight]);

  useEffect(() => {
    if (!svgRef.current || !longData.length) return;
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const g = svg
      .attr("width", innerW + margin.left + margin.right)
      .attr("height", height)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // ---- Zeitachse/Skalen
    const extentDates = d3.extent(longData, (d) => d.date);
    const xDomain = [
      d3.utcDay.offset(extentDates[0], -1),
      d3.utcDay.offset(extentDates[1], 1),
    ];

    const x = d3.scaleUtc().domain(xDomain).range([0, innerW]);

    const y = d3
      .scaleLinear()
      .domain([0, (d3.max(longData, (d) => d.value) || 100) * 1.2])
      .nice()
      .range([innerH, 0]);

    // Grid
    g.append("g")
      .attr("class", "grid")
      .call(d3.axisLeft(y).ticks(5).tickSize(-innerW).tickFormat(""))
      .selectAll("line")
      .attr("stroke", "#e5e7eb")
      .attr("stroke-opacity", 0.9);

    // Achsen
    const xAxisFormat = d3.utcFormat("%d.%m");
    g.append("g")
      .attr("transform", `translate(0,${innerH})`)
      .attr("class", "axis")
      .call(
        d3
          .axisBottom(x)
          .ticks(d3.timeDay.every(Math.ceil(data.length / 15)))
          .tickFormat(xAxisFormat)
      )
      .selectAll("text")
      .attr("font-size", 12);

    g.append("g")
      .attr("class", "axis")
      .call(d3.axisLeft(y).tickFormat((d) => `${d}%`))
      .selectAll("text")
      .attr("font-size", 12);

    const line = d3
      .line()
      .x((d) => x(d.date))
      .y((d) => y(d.value))
      .curve(d3.curveMonotoneX);

    const sortedSeries = Array.from(bySeries.keys()).sort((a, b) =>
      a === highlight ? -1 : b === highlight ? 1 : a.localeCompare(b)
    );

    sortedSeries.forEach((name) => {
      if (hidden.has(name)) return;
      const values = (bySeries.get(name) || [])
        .slice()
        .sort((a, b) => a.date - b.date);
      const isPrimary = name === highlight;

      const path = g
        .append("path")
        .datum(values)
        .attr("fill", "none")
        .attr("stroke", isPrimary ? "#4338ca" : color(name))
        .attr("stroke-width", isPrimary ? 3.5 : 2)
        .attr("stroke-opacity", isPrimary ? 1 : 0.85)
        .attr("d", line);

      const total = path.node()?.getTotalLength() || 0;
      path
        .attr("stroke-dasharray", `${total} ${total}`)
        .attr("stroke-dashoffset", total)
        .transition()
        .duration(900)
        .ease(d3.easeCubicOut)
        .attr("stroke-dashoffset", 0);

      const dots = g
        .selectAll(`.dot-${CSS.escape(name)}`)
        .data(values)
        .enter()
        .append("circle")
        .attr("class", `dot dot-${name}`)
        .attr("cx", (d) => x(d.date))
        .attr("cy", (d) => y(d.value))
        .attr("r", isPrimary ? 4.5 : 3.5)
        .attr("fill", isPrimary ? "#4338ca" : color(name))
        .attr("stroke", "#fff")
        .attr("stroke-width", 1.5)
        .attr("opacity", 0)
        .on("mouseenter", function (event, d) {
          d3.select(this).attr("opacity", 1);
          onHover?.({ name, value: d.value, date: d.date });
        })
        .on("mouseleave", function () {
          d3.select(this).attr("opacity", 0);
          onHover?.(null);
        });

      // Hover-Layer für Punkte
      g.append("rect")
        .attr("x", 0)
        .attr("y", 0)
        .attr("width", innerW)
        .attr("height", innerH)
        .attr("fill", "transparent")
        .on("mouseenter", () => dots.attr("opacity", 1))
        .on("mouseleave", () => dots.attr("opacity", 0));
    });

    // Notiz-Linien (nur sichtbarer Zeitraum)
    const visibleNotes = parsedNotes.filter(
      (n) => n.dateObj >= extentDates[0] && n.dateObj <= extentDates[1]
    );

    const noteTooltip = d3.select(noteTooltipRef.current);
    const noteTooltipFormat = d3.utcFormat("%d.%m.%Y");

    g.selectAll(".note-line")
      .data(visibleNotes)
      .enter()
      .append("line")
      .attr("class", "note-line")
      .attr("x1", (d) => x(d.dateObj))
      .attr("x2", (d) => x(d.dateObj))
      .attr("y1", 0)
      .attr("y2", innerH)
      .attr("stroke", "#4b5563")
      .attr("stroke-width", 1.5)
      .attr("stroke-dasharray", "4,4")
      .attr("opacity", 0.7)
      .style("cursor", "pointer")
      .on("mouseover", function (event, d) {
        d3.select(this).attr("stroke-width", 3).attr("opacity", 1);
        noteTooltip
          .style("opacity", 1)
          .html(
            `<strong>${noteTooltipFormat(d.dateObj)}</strong><br/>${String(
              d.description || ""
            ).replace(/\n/g, "<br/>")}`
          );
      })
      .on("mousemove", function (event) {
        const [posX, posY] = d3.pointer(event, wrapRef.current);
        noteTooltip.style("left", `${posX + 15}px`).style("top", `${posY}px`);
      })
      .on("mouseout", function () {
        d3.select(this).attr("stroke-width", 1.5).attr("opacity", 0.7);
        noteTooltip.style("opacity", 0);
      });

    // Legende (nur sichtbare Reihen)
    const legendX = innerW + 20;
    const legend = g
      .append("g")
      .attr("class", "legend")
      .attr("transform", `translate(${legendX}, 0)`);

    const items = sortedSeries.filter((s) => !hidden.has(s));
    const lg = legend
      .selectAll(".lg-item")
      .data(items)
      .enter()
      .append("g")
      .attr("class", "lg-item")
      .attr("transform", (_, i) => `translate(0, ${i * 22})`);

    lg.append("rect")
      .attr("width", 12)
      .attr("height", 12)
      .attr("rx", 2)
      .attr("fill", (d) => (d === highlight ? "#4338ca" : color(d)));

    lg.append("text")
      .attr("x", 18)
      .attr("y", 10)
      .attr("font-size", 12)
      .attr("fill", "#374151")
      .attr("font-weight", (d) => (d === highlight ? 700 : 400))
      .text((d) => d);
  }, [longData, bySeries, series, hidden, highlight, color, innerW, innerH, margin, parsedNotes, onHover, containerWidth, data.length]);

  return (
    <div ref={wrapRef} className="relative w-full overflow-x-auto">
      <svg
        ref={svgRef}
        role="img"
        aria-label="Entwicklung Anteil an möglichen Impressionen als Liniendiagramm"
      />
      <div
        ref={noteTooltipRef}
        className="absolute p-2 text-xs text-white bg-gray-800 bg-opacity-90 rounded-md shadow-lg pointer-events-none transition-opacity duration-200"
        style={{ opacity: 0, maxWidth: "220px" }}
      />
    </div>
  );
}

// ====== Hauptkomponente ======
export default function KdiTracker() {
  const [chartData, setChartData] = useState([]);
  const [reportDate, setReportDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [pastedData, setPastedData] = useState("");
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [hover, setHover] = useState(null);
  const [hiddenSeries, setHiddenSeries] = usePersistentState(
    "kdi:hiddenSeries",
    []
  );

  const [notes, setNotes] = useState([]);

  const [newNote, setNewNote] = useState({
    note_date: new Date().toISOString().split("T")[0],
    description: "",
  });

  const [isSavingNote, setIsSavingNote] = useState(false);
  const [timeRange, setTimeRange] = useState("all");

  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const hiddenSeriesSet = useMemo(() => new Set(hiddenSeries), [hiddenSeries]);

  const toggleSeries = (name) => {
    const next = new Set(hiddenSeries);
    if (next.has(name)) {
      next.delete(name);
    } else {
      next.add(name);
    }
    setHiddenSeries(Array.from(next));
  };

  const resetHidden = () => setHiddenSeries([]);

  const fetchData = async () => {
    setLoading(true);
    setMsg(null);
    try {
      const res = await fetch(GET_DATA_URL);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setChartData(data || []);
      if (!data?.length) {
        setMsg({
          type: "info",
          text: "Keine Datensätze gefunden. Füge unten einen ersten Bericht hinzu.",
        });
      }
    } catch (e) {
      console.error(e);
      setMsg({
        type: "error",
        text: "Daten konnten nicht geladen werden. (In der Vorschau erwartet)",
      });
      // Fallback-Daten für lokale Vorschau
      setChartData([
        { date: "2025-08-09", performance: { [PRIMARY_LABEL]: 13.58 } },
        { date: "2025-08-10", performance: { [PRIMARY_LABEL]: 10.6 } },
        { date: "2025-08-16", performance: { [PRIMARY_LABEL]: 8.8 } },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotes = async () => {
    try {
      const res = await fetch(GET_NOTES_URL);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.success) {
        setNotes(data.notes || []);
      } else {
        throw new Error(data.message || "Fehler beim Laden der Notizen.");
      }
    } catch (e) {
      console.error("Fehler beim Laden der Notizen:", e);
      setMsg({
        type: "error",
        text: `Notizen konnten nicht geladen werden: ${e.message}`,
      });
    }
  };

  useEffect(() => {
    fetchData();
    fetchNotes();
  }, []);

  const filteredData = useMemo(() => {
    if (!chartData) return [];
    const sortedData = [...chartData].sort(
      (a, b) => parseISO(a.date) - parseISO(b.date)
    );

    switch (timeRange) {
      case "7d": {
        const lastDate = sortedData[sortedData.length - 1]?.date;
        if (!lastDate) return [];
        const sevenDaysAgo = new Date(lastDate);
        sevenDaysAgo.setUTCDate(sevenDaysAgo.getUTCDate() - 6);
        return sortedData.filter((d) => parseISO(d.date) >= sevenDaysAgo);
      }
      case "30d": {
        const lastDate = sortedData[sortedData.length - 1]?.date;
        if (!lastDate) return [];
        const thirtyDaysAgo = new Date(lastDate);
        thirtyDaysAgo.setUTCDate(thirtyDaysAgo.getUTCDate() - 29);
        return sortedData.filter((d) => parseISO(d.date) >= thirtyDaysAgo);
      }
      case "all":
      default:
        return sortedData;
    }
  }, [chartData, timeRange]);

  const allSeries = useMemo(() => {
    const s = new Set();
    filteredData.forEach((d) =>
      Object.keys(d.performance || {}).forEach((k) => s.add(k))
    );
    return Array.from(s).sort((a, b) =>
      a === PRIMARY_LABEL ? -1 : b === PRIMARY_LABEL ? 1 : a.localeCompare(b)
    );
  }, [filteredData]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (!pastedData.trim()) {
      setMsg({
        type: "error",
        text: "Bitte füge Auktionsdaten in das Textfeld ein.",
      });
      return;
    }
    setLoading(true);
    try {
      const payload = { reportDate, pastedData };
      const res = await fetch(PROCESS_DATA_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(result?.message || `HTTP ${res.status}`);
      setMsg({
        type: "success",
        text: result?.message || "Daten gespeichert und verarbeitet.",
      });
      setPastedData("");
      await fetchData();
    } catch (e) {
      console.error(e);
      setMsg({ type: "error", text: `Fehler bei der Verarbeitung: ${e.message}` });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNote = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (!newNote.description.trim()) {
      setMsg({
        type: "error",
        text: "Bitte eine Beschreibung für die Notiz eingeben.",
      });
      return;
    }
    setIsSavingNote(true);
    try {
      const res = await fetch(SAVE_NOTE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newNote),
      });
      const result = await res.json().catch(() => ({}));
      if (!res.ok || !result.success) {
        throw new Error(result.message || `HTTP ${res.status}`);
      }
      setMsg({
        type: "success",
        text: result.message || "Notiz erfolgreich gespeichert.",
      });
      setNewNote({
        note_date: new Date().toISOString().split("T")[0],
        description: "",
      });
      await fetchNotes();
    } catch (e) {
      console.error(e);
      setMsg({
        type: "error",
        text: `Fehler beim Speichern der Notiz: ${e.message}`,
      });
    } finally {
      setIsSavingNote(false);
    }
  };

  const exportCSV = () => {
    const rows = [];
    chartData.forEach((d) => {
      const base = { date: d.date };
      Object.entries(d.performance || {}).forEach(([domain, value]) => {
        rows.push({ ...base, domain, value });
      });
    });
    const csv = toCSV(rows);
    downloadBlob(
      `kdi_auktionsdaten_${new Date().toISOString().slice(0, 10)}.csv`,
      "text/csv;charset=utf-8",
      csv
    );
  };

  const exportPNG = () => {
    const svg = document.querySelector("svg[aria-label]");
    downloadPNGFromSVG(
      svg,
      `kdi_chart_${new Date().toISOString().slice(0, 10)}.png`
    );
  };

  const copyExample = () => {
    const example = `Domain der angezeigten URL\tAnteil an möglichen Impressionen\n${PRIMARY_LABEL}\t13,58%\nbetterhelp.com\t31,33%\nhellobetter.de\t15,12%`;
    navigator.clipboard
      ?.writeText(example)
      .then(() =>
        setMsg({
          type: "info",
          text: "Beispieldaten in die Zwischenablage kopiert.",
        })
      );
  };

  const displayFormatDate = (dateString) => {
    const date = parseISO(dateString);
    return date ? d3.utcFormat("%d.%m.%Y")(date) : "Ungültiges Datum";
    ;
  };

  // ====== Render ======
  return (
    <div
      className="min-h-screen bg-gray-50 text-gray-900"
      style={{ padding: "0.5cm" }} // globaler ~0,5 cm Rand
    >
      <header className="sticky top-0 z-10 backdrop-blur bg-white/80 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold leading-tight">KDI Tracker</h1>
            <p className="text-xs text-gray-500">
              Google Ads Auktionsdaten · {VERSION} ·{" "}
              {currentTime.toLocaleDateString("de-DE")}{" "}
              {currentTime.toLocaleTimeString("de-DE", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportCSV}
              className="px-3 py-2 text-sm rounded-lg border bg-white hover:bg-gray-50"
            >
              CSV exportieren
            </button>
            <button
              onClick={exportPNG}
              className="px-3 py-2 text-sm rounded-lg border bg-white hover:bg-gray-50"
            >
              PNG exportieren
            </button>
          </div>
        </div>
      </header>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-8">
          {msg && (
            <div
              role="alert"
              aria-live="polite"
              className={cls(
                "rounded-xl p-3 text-sm border",
                msg.type === "success" &&
                  "bg-green-100 text-green-800 border-green-200",
                msg.type === "error" &&
                  "bg-red-100 text-red-800 border-red-200",
                msg.type === "info" &&
                  "bg-blue-100 text-blue-800 border-blue-200"
              )}
            >
              {msg.text}
            </div>
          )}

          {/* KPI-Chart */}
          <section className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 sm:px-6 sm:py-5 border-b border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold">
                  KPI-Vergleich: {PRIMARY_LABEL} vs. Konkurrenz
                </h2>
                <p className="text-sm text-gray-500">
                  Tägliche Entwicklung Ihres Anteils an möglichen Impressionen.
                </p>
              </div>

              <div className="flex items-center p-1 bg-gray-100 rounded-lg">
                <button
                  onClick={() => setTimeRange("7d")}
                  className={cls(
                    "px-3 py-1 text-sm font-medium rounded-md transition-colors",
                    timeRange === "7d"
                      ? "bg-blue-600 text-white"
                      : "text-gray-700 hover:bg-gray-200"
                  )}
                >
                  Letzte 7 Tage
                </button>
                <button
                  onClick={() => setTimeRange("30d")}
                  className={cls(
                    "px-3 py-1 text-sm font-medium rounded-md transition-colors",
                    timeRange === "30d"
                      ? "bg-blue-600 text-white"
                      : "text-gray-700 hover:bg-gray-200"
                  )}
                >
                  Letzter Monat
                </button>
                <button
                  onClick={() => setTimeRange("all")}
                  className={cls(
                    "px-3 py-1 text-sm font-medium rounded-md transition-colors",
                    timeRange === "all"
                      ? "bg-blue-600 text-white"
                      : "text-gray-700 hover:bg-gray-200"
                  )}
                >
                  Gesamte Zeit
                </button>
              </div>

              {hover ? (
                <div className="text-right text-sm">
                  <div className="font-medium">{hover.name}</div>
                  <div className="text-gray-600">
                    {d3.utcFormat("%d.%m.%Y")(hover.date)} · {fmtPct(hover.value)}
                  </div>
                </div>
              ) : (
                <div className="text-sm text-gray-500 hidden sm:block">
                  Hover für Details
                </div>
              )}
            </div>

            <div className="px-5 sm:px-6 py-6">
              {loading ? (
                <div className="h-[420px] animate-pulse rounded-xl bg-gradient-to-br from-gray-100 to-gray-50 border" />
              ) : filteredData?.length ? (
                <LinesChart
                  data={filteredData}
                  hidden={hiddenSeriesSet}
                  onHover={setHover}
                  notes={notes}
                />
              ) : (
                <div className="py-16 text-center text-gray-500">
                  <p className="mb-3">Keine Daten für den ausgewählten Zeitraum vorhanden.</p>
                  <button
                    onClick={() => setTimeRange("all")}
                    className="text-blue-700 hover:underline"
                  >
                    Gesamten Zeitraum anzeigen
                  </button>
                </div>
              )}

              {allSeries.length > 0 && (
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {allSeries.map((name) => (
                    <label
                      key={name}
                      className={cls(
                        "flex items-center gap-2 p-2 rounded-lg border",
                        name === PRIMARY_LABEL
                          ? "bg-indigo-50 border-indigo-200"
                          : "bg-white border-gray-200"
                      )}
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        checked={!hiddenSeriesSet.has(name)}
                        onChange={() => toggleSeries(name)}
                        aria-label={`${name} ein-/ausblenden`}
                      />
                      <span
                        className={cls(
                          "text-sm",
                          name === PRIMARY_LABEL && "font-semibold text-indigo-900"
                        )}
                      >
                        {name}
                      </span>
                    </label>
                  ))}
                </div>
              )}

              {hiddenSeries.length > 0 && (
                <div className="mt-3">
                  <button
                    onClick={resetHidden}
                    className="text-sm text-blue-700 hover:underline"
                  >
                    Alle Serien wieder einblenden
                  </button>
                </div>
              )}
            </div>
          </section>

          {/* Formulare + Notizen */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Daten-Import-Form */}
            <section className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-5 py-4 sm:px-6 sm:py-5 border-b border-gray-100 text-center">
                <h2 className="text-lg font-semibold">Neuen Tagesbericht hinzufügen</h2>
              </div>

              <form onSubmit={onSubmit} className="px-5 sm:px-6 py-6 space-y-6">
                <div>
                  <label
                    htmlFor="reportDate"
                    className="block text-sm font-medium text-gray-700 text-center mb-2"
                  >
                    1. Datum des Berichts
                  </label>
                  <input
                    id="reportDate"
                    type="date"
                    required
                    value={reportDate}
                    onChange={(e) => setReportDate(e.target.value)}
                    className="mt-1 block mx-auto w-full sm:w-2/3 md:w-1/2 rounded-lg border-gray-300 shadow-sm focus:border-blue-600 focus:ring-blue-600"
                  />
                </div>

                <div>
                  <label
                    htmlFor="pastedData"
                    className="block text-sm font-medium text-gray-700 text-center mb-2"
                  >
                    2. Auktionsdaten aus Google Ads einfügen
                  </label>
                  <textarea
                    id="pastedData"
                    rows={12}
                    value={pastedData}
                    onChange={(e) => setPastedData(e.target.value)}
                    placeholder="Kopieren Sie die Auktionsdaten (inkl. Kopfzeile) hier hinein."
                    className="mt-1 block w-full rounded-xl border border-gray-300 shadow-sm focus:border-blue-600 focus:ring-blue-600 font-mono text-[12px] p-3 resize-y"
                  />
                </div>

                <div className="pt-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-center gap-6">
                  <button
                    type="submit"
                    disabled={loading}
                    className={cls(
                      "inline-flex items-center justify-center px-6 py-3 rounded-lg text-white font-semibold transition",
                      loading ? "bg-blue-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
                    )}
                  >
                    {loading ? "Verarbeite…" : "Daten verarbeiten"}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setReportDate(new Date().toISOString().split("T")[0]);
                      setPastedData("");
                    }}
                    className="px-6 py-3 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold"
                  >
                    Zurücksetzen
                  </button>

                  <button
                    type="button"
                    onClick={copyExample}
                    className="px-6 py-3 rounded-lg bg-white border hover:bg-gray-50 text-gray-700 font-semibold"
                  >
                    Beispieldaten kopieren
                  </button>
                </div>
              </form>
            </section>

            {/* Notiz-Form separat */}
            <section className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-5 py-4 sm:px-6 sm:py-5 border-b border-gray-100 text-center">
                <h2 className="text-lg font-semibold">Notizen zu Änderungen hinzufügen</h2>
              </div>

              <form
                onSubmit={handleSaveNote}
                className="px-5 sm:px-6 py-6 space-y-6"
              >
                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="note_date"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Datum der Änderung
                    </label>
                    <input
                      id="note_date"
                      type="date"
                      required
                      value={newNote.note_date}
                      onChange={(e) =>
                        setNewNote({ ...newNote, note_date: e.target.value })
                      }
                      className="mt-1 w-full sm:w-2/3 md:w-1/2 rounded-lg border-gray-300 shadow-sm focus:border-blue-600 focus:ring-blue-600"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="description"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Was wurde geändert?
                    </label>
                    <textarea
                      id="description"
                      rows={3}
                      required
                      value={newNote.description}
                      onChange={(e) =>
                        setNewNote({ ...newNote, description: e.target.value })
                      }
                      placeholder="z.B. CPA von 18€ auf 17€ gesenkt."
                      className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-600 focus:ring-blue-600"
                    />
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-100">
                  <button
                    type="submit"
                    disabled={isSavingNote}
                    className={cls(
                      "inline-flex items-center justify-center px-5 py-2.5 rounded-lg text-white font-semibold transition text-sm",
                      isSavingNote
                        ? "bg-blue-400 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700"
                    )}
                  >
                    {isSavingNote ? "Speichere..." : "Notiz speichern"}
                  </button>
                </div>
              </form>
            </section>
          </div>

          {/* Ausgelagerte scrollbare Notiz-Liste */}
          <section className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 sm:px-6 sm:py-5 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Vorhandene Notizen</h2>
              <span className="text-sm text-gray-500">
                {notes?.length || 0} Einträge
              </span>
            </div>

            <div className="px-5 sm:px-6 py-4">
              {notes?.length ? (
                <div className="max-h-[50vh] overflow-y-auto pr-2 space-y-3">
                  {notes
                    .slice()
                    .sort(
                      (a, b) =>
                        new Date(b.note_date).getTime() -
                        new Date(a.note_date).getTime()
                    )
                    .map((note) => (
                      <div
                        key={note.id ?? `${note.note_date}-${note.description?.slice(0,20)}`}
                        className="text-sm p-3 bg-gray-50 rounded-lg border border-gray-200"
                      >
                        <p className="font-semibold text-gray-700">
                          {displayFormatDate(note.note_date)}
                        </p>
                        <p className="text-gray-600 whitespace-pre-wrap mt-1">
                          {note.description}
                        </p>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-gray-500">Noch keine Notizen vorhanden.</p>
              )}
            </div>
          </section>

          <footer className="pt-4">
            <div
              className="back-button-wrapper"
              style={{
                display: "flex",
                justifyContent: "flex-start",
                marginBottom: "10px",
              }}
            >
              <button
                onClick={() => window.history.back()}
                className="back-button"
                style={{
                  backgroundColor: "#a6c8ff",
                  color: "#ffffff",
                  border: "none",
                  padding: "8px 16px",
                  fontSize: "16px",
                  fontWeight: "bold",
                  cursor: "pointer",
                  borderRadius: "6px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "fit-content",
                  transition: "background-color 0.3s ease",
                }}
                onMouseOver={(e) =>
                  (e.currentTarget.style.backgroundColor = "#78aaff")
                }
                onMouseOut={(e) =>
                  (e.currentTarget.style.backgroundColor = "#a6c8ff")
                }
                aria-label="Zurück zur vorherigen Seite"
              >
                Zurück
              </button>
            </div>
          </footer>

          <p className="text-xs text-gray-500 text-center">
            Kommuniziert mit <code>{GET_DATA_URL}</code>,{" "}
            <code>{PROCESS_DATA_URL}</code> und{" "}
            <code>/api/kdi_notes_handler.php</code>.
          </p>
        </main>
    </div>
  );

}
